import { Card } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Users, FileEdit, ArrowUpDown, Clock, Car, CheckCircle, XCircle, Target } from "lucide-react";
import { useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { fromZonedTime } from 'date-fns-tz';

interface AdvisorMetricsDashboardProps {
  shopId: string;
  dateISO: string;
  shopTimezone: string;
}

export function AdvisorMetricsDashboard({ shopId, dateISO, shopTimezone }: AdvisorMetricsDashboardProps) {
  const queryClient = useQueryClient();

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['advisor-metrics', shopId, dateISO, shopTimezone],
    queryFn: async () => {
      // Try pre-aggregated data first (fast)
      const { data, error } = await supabase
        .from('advisor_daily_metrics')
        .select('*')
        .eq('shop_id', shopId)
        .eq('date', dateISO);

      if (error) throw error;

      // If we have pre-aggregated data, use it
      if (data && data.length > 0) {
        return data.reduce((acc, curr) => ({
          tickets_created: (acc.tickets_created || 0) + (curr.tickets_created || 0),
          tickets_edited: (acc.tickets_edited || 0) + (curr.tickets_edited || 0),
          waiter_count: (acc.waiter_count || 0) + (curr.waiter_count || 0),
          dropoff_count: (acc.dropoff_count || 0) + (curr.dropoff_count || 0),
          waiter_conversions: (acc.waiter_conversions || 0) + (curr.waiter_conversions || 0),
          scheduling_overrides: (acc.scheduling_overrides || 0) + (curr.scheduling_overrides || 0),
          jobs_approved: (acc.jobs_approved || 0) + (curr.jobs_approved || 0),
          jobs_declined: (acc.jobs_declined || 0) + (curr.jobs_declined || 0),
          total_ticket_value_minutes: (acc.total_ticket_value_minutes || 0) + (curr.total_ticket_value_minutes || 0),
        }), {
          tickets_created: 0,
          tickets_edited: 0,
          waiter_count: 0,
          dropoff_count: 0,
          waiter_conversions: 0,
          scheduling_overrides: 0,
          jobs_approved: 0,
          jobs_declined: 0,
          total_ticket_value_minutes: 0,
        });
      }

      // FALLBACK: Query tickets table directly for real-time counts
      // R14-L-1: Convert shop-local day bounds to UTC
      // FIX-R131-MED-3: previously missing `.neq('status', 'Deleted')`. When
      // `advisor_daily_metrics` has no pre-aggregated row for the day, the
      // fallback inflated `tickets_created`, `waiter_count`/`dropoff_count`,
      // and `total_ticket_value_minutes` with deleted tickets created the
      // same day. Same bug class as R131-MED-1 in ShopMetricsTrends.
      const startUtc = fromZonedTime(`${dateISO}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${dateISO}T23:59:59`, shopTimezone).toISOString();

      const { data: ticketsData, error: ticketsError } = await supabase
        .from('tickets')
        .select('id, type, predicted_minutes')
        .eq('shop_id', shopId)
        // DMC-D3a: canonical active-ticket filter — both reps
        // excluded. R131-MED-3 status filter retained; deleted_at
        // exclusion added as defense-in-depth atop the DMC-D1
        // primitive fix.
        .neq('status', 'Deleted')
        .is('deleted_at', null)
        .gte('created_at', startUtc)
        .lte('created_at', endUtc);

      if (ticketsError) throw ticketsError;

      // Calculate metrics from raw tickets
      const waiterCount = ticketsData?.filter(t => t.type === 'Waiter').length || 0;
      const dropoffCount = ticketsData?.filter(t => t.type === 'Drop-off').length || 0;
      const totalMinutes = ticketsData?.reduce((sum, t) => 
        sum + (t.predicted_minutes || 0), 0) || 0;

      return {
        tickets_created: ticketsData?.length || 0,
        tickets_edited: 0,
        waiter_count: waiterCount,
        dropoff_count: dropoffCount,
        waiter_conversions: 0,
        scheduling_overrides: 0,
        jobs_approved: 0,
        jobs_declined: 0,
        total_ticket_value_minutes: totalMinutes,
      };
    }
  });

  // Fetch promise accuracy from waiter_promise_tracking
  const { data: promiseData } = useQuery({
    queryKey: ['advisor-promise-accuracy', shopId, dateISO, shopTimezone],
    queryFn: async () => {
      // R14-L-1: Convert shop-local day bounds to UTC
      const startUtc = fromZonedTime(`${dateISO}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${dateISO}T23:59:59`, shopTimezone).toISOString();

      const { data, error } = await supabase
        .from('waiter_promise_tracking')
        .select('on_time, created_by_user_id')
        .eq('shop_id', shopId)
        .gte('created_at', startUtc)
        .lte('created_at', endUtc);

      if (error) throw error;

      const total = data?.length || 0;
      const onTime = data?.filter(d => d.on_time === true).length || 0;
      const late = data?.filter(d => d.on_time === false).length || 0;
      const accuracy = total > 0 ? (onTime / total) * 100 : null;

      return { total, onTime, late, accuracy };
    }
  });

  // Realtime subscription
  useEffect(() => {
    // Chronomancer R-RT-4: scope channel name with shop id.
    const channel = supabase
      .channel(`advisor-metrics-changes-${shopId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'advisor_daily_metrics',
          filter: `shop_id=eq.${shopId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['advisor-metrics', shopId, dateISO] });
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[AdvisorMetricsDashboard] realtime status:', status, err);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shopId, dateISO, queryClient]);

  if (isLoading) {
    return <Card className="p-4"><div className="h-48 animate-pulse bg-muted rounded" /></Card>;
  }

  if (!metrics) {
    return null;
  }

  const conversionRate = metrics.waiter_count > 0 
    ? (metrics.waiter_conversions / metrics.waiter_count) * 100 
    : 0;

  // Calculate avg sold hours per ticket
  const avgSoldHoursPerTicket = metrics.tickets_created > 0
    ? (metrics.total_ticket_value_minutes / metrics.tickets_created) / 60
    : 0;

  // Calculate waiter/drop-off mix
  const totalTickets = metrics.waiter_count + metrics.dropoff_count;
  const waiterPercent = totalTickets > 0 ? (metrics.waiter_count / totalTickets) * 100 : 0;
  const dropoffPercent = totalTickets > 0 ? (metrics.dropoff_count / totalTickets) * 100 : 0;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <BarChart className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Advisor Activity</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Tickets Created</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{metrics.tickets_created}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <FileEdit className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Tickets Edited</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{metrics.tickets_edited}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpDown className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Overrides</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{metrics.scheduling_overrides}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Conversions</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{metrics.waiter_conversions}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Avg Sold Hrs/Ticket</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{avgSoldHoursPerTicket.toFixed(2)}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Waiter Conversion Rate</span>
            <span className="font-mono text-sm font-medium text-foreground">
              {conversionRate.toFixed(0)}%
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Value (hrs)</span>
            <span className="font-mono text-sm font-medium text-foreground">
              {((metrics.total_ticket_value_minutes || 0) / 60).toFixed(1)}
            </span>
          </div>
        </div>
      </Card>

      {/* Waiter vs Drop-Off Mix */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Car className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Ticket Type Mix</h3>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-warning" />
              <p className="text-xs text-muted-foreground">Waiters</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{metrics.waiter_count}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Car className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Drop-offs</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{metrics.dropoff_count}</p>
          </div>
        </div>

        {totalTickets > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Mix Distribution</span>
              <span className="font-mono text-foreground">
                Waiters {waiterPercent.toFixed(0)}% / Drop-offs {dropoffPercent.toFixed(0)}%
              </span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-warning transition-all" 
                style={{ width: `${waiterPercent}%` }}
              />
              <div 
                className="h-full bg-primary transition-all" 
                style={{ width: `${dropoffPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-warning rounded" /> Waiters
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-primary rounded" /> Drop-offs
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Promise Accuracy KPI */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Waiter Promise Accuracy</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Waiter Jobs</p>
            </div>
            <p className="font-mono text-xl font-bold text-foreground">{promiseData?.total || 0}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="h-4 w-4 text-success" />
              <p className="text-xs text-muted-foreground">On-Time</p>
            </div>
            <p className="font-mono text-xl font-bold text-success">{promiseData?.onTime || 0}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-4 w-4 text-destructive" />
              <p className="text-xs text-muted-foreground">Late</p>
            </div>
            <p className="font-mono text-xl font-bold text-destructive">{promiseData?.late || 0}</p>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-accent" />
              <p className="text-xs text-muted-foreground">Accuracy</p>
            </div>
            <p className={`font-mono text-xl font-bold ${
              promiseData?.accuracy != null 
                ? promiseData.accuracy >= 85 
                  ? 'text-success' 
                  : promiseData.accuracy >= 70 
                    ? 'text-warning' 
                    : 'text-destructive'
                : 'text-muted-foreground'
            }`}>
              {promiseData?.accuracy != null ? `${promiseData.accuracy.toFixed(0)}%` : 'N/A'}
            </p>
          </div>
        </div>

        {promiseData && promiseData.total > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Promise Performance</span>
              <span className={`font-mono ${
                promiseData.accuracy != null && promiseData.accuracy >= 85 
                  ? 'text-success' 
                  : promiseData.accuracy != null && promiseData.accuracy >= 70 
                    ? 'text-warning' 
                    : 'text-destructive'
              }`}>
                {promiseData.accuracy != null ? `${promiseData.accuracy.toFixed(0)}% on-time` : 'No data'}
              </span>
            </div>
            <Progress 
              value={promiseData.accuracy ?? 0} 
              className="h-2"
            />
          </div>
        )}
      </Card>
    </div>
  );
}
